<template>
  <v-dialog
    v-model="editStageDialog"
    no-click-animation
    overlay-color="rgba(39, 50, 64)"
    overlay-opacity="0.6"
    :fullscreen="$vuetify.breakpoint.xs ? true : false"
    persistent
    width="650"
    :transition="false"
  >
    <v-card>
      <v-card-title class="pl-5">
        {{ $t('modules.roadmap.stageRoadmaps') }}
      </v-card-title>
      <v-card-text>
        <v-layout>
          <v-flex
            mt-2
            pr-4
          >
            <v-btn
              v-if="!editForm"
              :disabled="$apollo.loading"
              small
              style="float:right;"
              outlined
              @click="editForm = true"
            >
              <v-icon>mdi-plus-circle-outline</v-icon>
            </v-btn>
          </v-flex>
        </v-layout>
        <v-data-table
          v-if="moduleRoadmapStages && !editForm"
          height="300"
          class="text-no-wrap mb-7" 
          :headers="headers"
          :items="moduleRoadmapStages"
          :hide-default-footer="true"
          :mobile-breakpoint="0"
          :items-per-page="200"
        >
          <template
            slot="headerCell"
            slot-scope="{ header }"
          >
            <span
              v-text="header.text"
            />
          </template>
          <template v-slot:item.name="{ item }">
            {{ item.name }}
          </template>
          <template v-slot:item.created_at="{ item }">
            <small>{{ item.created_at | moment("L") }}</small>
          </template>
          <template v-slot:item.actions="{ item }">
            <div class="text-right">
              <v-btn
                :disabled="$apollo.loading"
                class="mr-2"
                outlined 
                small
                text
                @click="editStage(item)"
              >
                <v-icon>mdi-pencil-outline</v-icon>
              </v-btn>
              <v-btn
                :disabled="$apollo.loading"
                outlined
                small
                text
                @click="deleteStage(item)"
              >
                <v-icon>mdi-bucket-outline</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
        <v-form v-if="editForm">
          <v-layout wrap>
            <v-flex
              mt-4
              xs12
            >
              <v-text-field
                v-model.trim="form.name"
                :label="$t('modules.roadmap.headerStages.name')"
                :error-messages="NameError"
                @blur="$v.form.name.$touch()"
              />
            </v-flex>
            <v-flex class="text-right">
              <v-btn
                text
                :disabled="loading"
                @click="editForm = false; editMode = false;"
              >
                <span>{{ $t('globals.cancelButton') }}</span>
              </v-btn>
              <v-btn
                color="primary"
                :disabled="loading || ($v.$invalid)"
                :loading="loading"
                @click="save()"
              >
                {{ $t(!editMode ? 'globals.addButton' : 'globals.saveButton') }}
              </v-btn>
            </v-flex>
          </v-layout>
        </v-form>
      </v-card-text>
      <v-divider />
      <v-card-actions v-if="!editForm">
        <v-spacer />
        <v-btn
          text
          outlined
          @click="editStageDialog = false"
        >
          {{ $t('globals.closeButton') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ROADMAP_STAGES_QUERY from "@/graphql/queries/modules/RoadmapStages.gql";

import STAGE_CREATE_MUTATION from "@/graphql/mutations/modules/RoadmapStageCreate.gql";
import STAGE_UPDATE_MUTATION from "@/graphql/mutations/modules/RoadmapStageUpdate.gql";
import STAGE_DELETE_MUTATION from "@/graphql/mutations/modules/RoadmapStageDelete.gql";

import { validationMixin } from "vuelidate";
import {
  required,
  minLength,
  maxLength
} from "vuelidate/lib/validators";
import { mapGetters } from 'vuex'

export default {
  name: "EditStageDialog",
  mixins: [validationMixin],
  props: {
    showDialog: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      loading: false,
      editForm: false,
      editMode: false,
      selectedItem: null,
      form: {},
      headers: [
        {
          sortable: false,
          text: this.$t('modules.roadmap.headerStages.name'),
          value: 'name'
        },
        {
          sortable: false,
          text: this.$t('modules.roadmap.headerStages.addedAt'),
          value: 'created_at'
        },
        {
          sortable: false,
          text: '',
          value: 'actions',
        },
      ]
    }
  },
  validations() {
    return {
      form: {
        name: {
          required,
          minLength: minLength(1),
          maxLength: maxLength(50)
        }
      }
    };
  },
  computed: {
    ...mapGetters("whitelabel", ["getWhiteLabel"]),
    editStageDialog: {
      get() {
        return this.showDialog;
      },
      set(val) {
        this.$emit("closeEditStageDialog", val);
      }
    },
    NameError() {
      const errors = [];
      if (!this.$v.form.name.$dirty) return errors;
      !this.$v.form.name.required &&
        errors.push(this.$t("globals.reqField"));
      !this.$v.form.name.minLength &&
        errors.push(this.$t("globals.minError", {char: this.$v.form.name.$params.minLength.min}));
      !this.$v.form.name.maxLength &&
        errors.push(this.$t("globals.maxError", {char: this.$v.form.name.$params.maxLength.max}));
      return errors;
    }
  },
  watch: {
    editMode(val) {
      if (!val) {
        this.selectedItem = null
      }
    },
    editForm(val) {
      if (!this.editMode) {
        this.form = {
          name: ""
        }
        this.$v.$reset();
      }
    }
  },
  methods: {
    open() {
      this.dialog = true;
    },
    editStage(item) {
      this.editForm = true
      this.editMode = true
      this.selectedItem = item
      this.form = {
        name: item.name,
      }
    },
    save() {
      this.$v.$touch();
      if (this.$v.$invalid) return;

      this.loading = true;
      const params = {
        whitelabel_uuid: this.getWhiteLabel.uuid,
        ...this.form,
      }
      if (this.editMode) {
        params.stage_uuid = this.selectedItem.uuid
      }

      this.$apollo
        .mutate({
          mutation: this.editMode ? STAGE_UPDATE_MUTATION : STAGE_CREATE_MUTATION,
          variables: params
        })
        .then(data => {
          this.$apollo.queries.moduleRoadmapStages.refetch()
          this.editForm = false
          this.editMode = false
          this.loading = false
        })
        .catch(error => {
          this.errorHandler(error)
          this.loading = false
        });
    },
    deleteStage(item) {
      const params = {
        whitelabel_uuid: this.getWhiteLabel.uuid,
        stage_uuid: item.uuid
      }
      this.$apollo
        .mutate({
          mutation: STAGE_DELETE_MUTATION,
          variables: params
        })
        .then(data => {
          this.$apollo.queries.moduleRoadmapStages.refetch()
          this.loading = false
        })
        .catch(error => {
          this.errorHandler(error)
          this.loading = false
        });
    }
  },
  apollo: {
    moduleRoadmapStages: {
      fetchPolicy: "network-only",
      query() {
        return  ROADMAP_STAGES_QUERY;
      },
      variables() {
        return {
          whitelabel_uuid: this.getWhiteLabel.uuid
        }
      },
      result({loading, data}) {
        if (!loading && (!data.moduleRoadmapStages || data.moduleRoadmapStages.length == 0) ) {
          this.editForm = true
        }
      },
      skip() {
        return !this.getWhiteLabel;
      },
    }
  },
};
</script>

<style lang="scss" scoped>
</style>
